import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack,
  IconButton,
  Collapse
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Notes as NotesIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  LocalHospital as EncounterIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { getPatientEncounterHistory } from '../../services/encounterHistoryService';

const NOTE_TYPES = [
  { value: 'all', label: 'All Notes' },
  { value: 'consultationNotes', label: 'Consultation Notes' },
  { value: 'progressNotes', label: 'Progress Notes' },
  { value: 'nurseNotes', label: 'Nurse Notes' },
  { value: 'procedureNotes', label: 'Procedure Notes' },
  { value: 'phoneNotes', label: 'Phone Notes' },
  { value: 'simpleNotes', label: 'Simple Notes' },
  { value: 'soapNotes', label: 'SOAP Notes' },
  { value: 'chiefComplaint', label: 'Chief Complaint' },
  { value: 'initialAssessment', label: 'Initial Assessment' },
  { value: 'actionPlan', label: 'Action Plan' },
  // Emergency Notes
  { value: 'triageNotes', label: 'Triage Notes' },
  { value: 'emergencyAssessment', label: 'Emergency Assessment' },
  { value: 'treatmentPlan', label: 'Treatment Plan' },
  { value: 'dispositionNotes', label: 'Disposition Notes' },
  // Surgical Notes
  { value: 'preOpNotes', label: 'Pre-Operative Notes' },
  { value: 'surgicalPlan', label: 'Surgical Plan' },
  { value: 'anesthesiaNotes', label: 'Anesthesia Notes' },
  { value: 'postOpNotes', label: 'Post-Operative Notes' },
  // Observation Notes
  { value: 'admissionNotes', label: 'Admission Notes' },
  { value: 'dischargeNotes', label: 'Discharge Notes' },
  // Additional Notes
  { value: 'secondOpinionNotes', label: 'Second Opinion Notes' }
];

const ITEMS_PER_PAGE = 10;

const PatientNotesHistoryItem = ({ historyRecord, noteType, noteContent, isExpanded, onToggle }) => {
  const formatDate = (date) => {
    if (!date) return 'Unknown Date';
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return format(dateObj, 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatEncounterDate = (date) => {
    if (!date) return 'Unknown';
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return format(dateObj, 'MMM dd, yyyy');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getChangeTypeColor = (changeType) => {
    switch (changeType) {
      case 'create': return 'success';
      case 'update': return 'primary';
      case 'note_added': return 'info';
      default: return 'default';
    }
  };

  const getNoteTypeLabel = (type) => {
    // Handle SOAP note sub-types
    if (type.startsWith('soapNotes.')) {
      const soapType = type.split('.')[1];
      const soapLabels = {
        subjective: 'SOAP - Subjective',
        objective: 'SOAP - Objective', 
        assessment: 'SOAP - Assessment',
        plan: 'SOAP - Plan'
      };
      return soapLabels[soapType] || `SOAP - ${soapType}`;
    }
    
    const noteTypeObj = NOTE_TYPES.find(nt => nt.value === type);
    return noteTypeObj ? noteTypeObj.label : type;
  };

  const getProviderInfo = () => {
    const provider = historyRecord.clinicalData?.provider;
    if (provider && provider.name) {
      return `${provider.name}${provider.role ? ` (${provider.role})` : ''}`;
    }
    return 'Unknown Provider';
  };

  const getEncounterTypeLabel = (type) => {
    const typeLabels = {
      'outpatient': 'Outpatient Visit',
      'inpatient': 'Inpatient',
      'emergency': 'Emergency',
      'observation': 'Observation',
      'surgical': 'Surgical',
      'telehealth': 'Telehealth'
    };
    return typeLabels[type] || type || 'Visit';
  };

  // Truncate long content for preview
  const truncateContent = (content, maxLength = 150) => {
    if (!content || content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  // Get encounter context from history record
  const encounterType = historyRecord.encounterType || historyRecord.clinicalData?.encounterType;
  const encounterDate = historyRecord.encounterDate || historyRecord.clinicalData?.admissionDate;
  const department = historyRecord.department || historyRecord.clinicalData?.department;

  return (
    <Card sx={{ mb: 2, border: '1px solid', borderColor: 'grey.200' }}>
      <CardContent sx={{ pb: '16px !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <NotesIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {getNoteTypeLabel(noteType)}
              </Typography>
              <Chip 
                label={historyRecord.changeType} 
                size="small" 
                color={getChangeTypeColor(historyRecord.changeType)}
                variant="outlined"
              />
              <Typography variant="caption" color="text.secondary">
                v{historyRecord.version}
              </Typography>
            </Stack>
            
            {/* Encounter Context */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5, 
              mb: 1,
              backgroundColor: 'grey.50',
              borderRadius: 1,
              px: 1,
              py: 0.5
            }}>
              <EncounterIcon sx={{ fontSize: 14, color: 'primary.main' }} />
              <Typography variant="caption" color="primary.main" sx={{ fontWeight: 500 }}>
                {getEncounterTypeLabel(encounterType)}
              </Typography>
              {encounterDate && (
                <>
                  <Typography variant="caption" color="text.secondary">•</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatEncounterDate(encounterDate)}
                  </Typography>
                </>
              )}
              {department && (
                <>
                  <Typography variant="caption" color="text.secondary">•</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {department}
                  </Typography>
                </>
              )}
            </Box>
            
            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {formatDate(historyRecord.changedAt)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {getProviderInfo()}
                </Typography>
              </Box>
            </Stack>
          </Box>
          
          <IconButton 
            size="small" 
            onClick={onToggle}
            sx={{ ml: 1 }}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Note Content Preview */}
        <Box sx={{ mb: 1 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              whiteSpace: 'pre-wrap',
              color: 'text.secondary',
              fontStyle: noteContent ? 'normal' : 'italic'
            }}
          >
            {noteContent ? truncateContent(noteContent) : 'No content'}
          </Typography>
        </Box>

        {/* Expanded Content */}
        <Collapse in={isExpanded}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {noteContent || 'No content available'}
          </Typography>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const PatientNotesHistoryTab = ({ patientId, facilityId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [page, setPage] = useState(1);
  const [expandedItems, setExpandedItems] = useState(new Set());

  useEffect(() => {
    const fetchHistory = async () => {
      if (!patientId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Get history across all encounters for this patient
        const historyData = await getPatientEncounterHistory(patientId, 200, facilityId);
        setHistory(historyData || []);
      } catch (err) {
        console.error('Error fetching patient encounter history:', err);
        setError('Failed to load notes history');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [patientId, facilityId]);

  // Extract and flatten all notes from history records
  const extractNotesFromHistory = () => {
    const allNotes = [];
    
    if (!Array.isArray(history)) return allNotes;
    
    history.forEach(record => {
      const clinicalData = record.clinicalData || {};
      
      // Extract different types of notes
      if (clinicalData.notes) {
        // Individual note types
        Object.entries(clinicalData.notes).forEach(([noteType, content]) => {
          if (noteType === 'soapNotes' && content && typeof content === 'object') {
            // Handle SOAP notes separately
            Object.entries(content).forEach(([soapType, soapContent]) => {
              if (soapContent && soapContent.trim()) {
                allNotes.push({
                  ...record,
                  noteType: `soapNotes.${soapType}`,
                  noteContent: soapContent,
                  sortKey: record.changedAt
                });
              }
            });
          } else if (content && content.trim()) {
            allNotes.push({
              ...record,
              noteType,
              noteContent: content,
              sortKey: record.changedAt
            });
          }
        });
      }
      
      // Extract other clinical fields
      ['chiefComplaint', 'initialAssessment', 'actionPlan'].forEach(field => {
        if (clinicalData[field] && clinicalData[field].trim()) {
          allNotes.push({
            ...record,
            noteType: field,
            noteContent: clinicalData[field],
            sortKey: record.changedAt
          });
        }
      });
    });
    
    // Sort by date (latest first)
    return allNotes.sort((a, b) => {
      const dateA = a.sortKey?.toDate ? a.sortKey.toDate() : new Date(a.sortKey);
      const dateB = b.sortKey?.toDate ? b.sortKey.toDate() : new Date(b.sortKey);
      return dateB - dateA;
    });
  };

  // Filter notes based on selected type
  const filterNotes = (notes) => {
    if (filterType === 'all') return notes;
    
    return notes.filter(note => {
      if (filterType === 'soapNotes') {
        return note.noteType.startsWith('soapNotes.');
      }
      return note.noteType === filterType;
    });
  };

  const allNotes = extractNotesFromHistory();
  const filteredNotes = filterNotes(allNotes);
  
  // Pagination
  const totalPages = Math.ceil(filteredNotes.length / ITEMS_PER_PAGE);
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const paginatedNotes = filteredNotes.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleFilterChange = (event) => {
    setFilterType(event.target.value);
    setPage(1); // Reset to first page when filter changes
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const toggleExpanded = (noteId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(noteId)) {
      newExpanded.delete(noteId);
    } else {
      newExpanded.add(noteId);
    }
    setExpandedItems(newExpanded);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading notes history...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (allNotes.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <NotesIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No notes history found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Clinical notes from patient encounters will appear here once they are saved.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Filter */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Notes History ({filteredNotes.length} {filteredNotes.length === 1 ? 'entry' : 'entries'})
        </Typography>
        
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Note Type</InputLabel>
          <Select
            value={filterType}
            onChange={handleFilterChange}
            label="Filter by Note Type"
          >
            {NOTE_TYPES.map(type => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Notes List */}
      {filteredNotes.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No notes found for the selected filter.
          </Typography>
        </Box>
      ) : (
        <>
          {paginatedNotes.map((note, index) => {
            const noteId = `${note.id}-${note.encounterId}-${note.noteType}-${index}`;
            return (
              <PatientNotesHistoryItem
                key={noteId}
                historyRecord={note}
                noteType={note.noteType}
                noteContent={note.noteContent}
                isExpanded={expandedItems.has(noteId)}
                onToggle={() => toggleExpanded(noteId)}
              />
            );
          })}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PatientNotesHistoryTab;
